import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9qamfcnlsngrm4llun0';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormSubmitting(false);
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, value as string);
        }
      });

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // raw text
      }

      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
        || parsed?.message as string
        || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormError('提交失败，请稍后重试');
        setFormStatus('error');
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    } finally {
      setFormSubmitting(false);
      setTimeout(() => setFormStatus('idle'), 4000);
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '大满贯App',
        url: 'https://damanjuapp.com',
        description: '大满贯App - 专业的体育赛事直播与数据分析平台，提供全球主流赛事高清直播、实时数据更新和深度战术分析。',
        foundingDate: '2020',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '建国路88号SOHO现代城A座',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          postalCode: '100022',
          addressCountry: 'CN',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+86-400-888-6666',
          contactType: 'customer service',
          availableLanguage: ['Chinese', 'English'],
        },
      },
      {
        '@type': 'WebPage',
        name: '关于大满贯App - 品牌介绍与联系方式',
        url: 'https://damanjuapp.com/about',
        isPartOf: { '@type': 'WebSite', name: '大满贯App' },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于大满贯 - 品牌介绍与联系方式"
        description="大满贯App官方品牌介绍，了解我们的发展历程、口碑声誉和联系方式。专业的体育赛事直播平台，服务超过5000万用户。"
        keywords="大满贯app,关于大满贯,体育app品牌,大满贯联系方式,大满贯口碑"
        canonicalUrl="https://damanjuapp.com/about"
        schema={aboutSchema}
      />
      <Breadcrumb />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Page Title */}
        <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-2">
          {t('about.title')}
        </h1>
        <p className="text-foreground-600 text-base mb-10">了解大满贯的品牌故事与发展历程</p>

        {/* Brand Introduction */}
        <section id="brand" className="mb-16" aria-labelledby="brand-title">
          <h2 id="brand-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
            {t('about.brand')}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="rounded-2xl overflow-hidden mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20tech%20office%20with%20team%20collaborating%20on%20sports%20analytics%2C%20warm%20lighting%2C%20large%20screens%20showing%20sports%20data%2C%20professional%20environment%2C%20Chinese%20tech%20company%2C%20red%20and%20gold%20brand%20accents%2C%20clean%20modern%20design&width=700&height=450&seq=about-brand-01&orientation=landscape"
                  alt="大满贯App品牌 - 专业体育科技团队"
                  title="大满贯App - 品牌介绍"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <p className="text-foreground-700 leading-relaxed mb-4">
                <strong className="text-foreground-950">大满贯App</strong>成立于2020年，是一家专注于体育赛事直播与数据分析的科技公司。我们致力于为全球体育爱好者提供高品质、低延迟的赛事直播服务，以及专业、深度的赛事数据分析。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                经过多年的技术积累和产品打磨，大满贯App已经发展成为国内领先的体育直播平台之一。我们的团队由资深体育媒体人、数据科学家和顶尖工程师组成，共同打造了这款集<strong className="text-foreground-950">赛事直播、实时数据、精准分析、互动社区</strong>于一体的综合性体育App。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                大满贯App覆盖足球、篮球、网球、电竞、田径等超过200项体育赛事，与全球多家顶级体育联盟和赛事组织建立了深度合作关系，确保为用户提供最全面、最及时的赛事内容。
              </p>
              <div className="flex flex-wrap gap-4 mt-2">
                <div className="px-4 py-2 rounded-lg bg-primary-100 text-primary-700 text-sm font-medium whitespace-nowrap">
                  5000万+ 用户信赖
                </div>
                <div className="px-4 py-2 rounded-lg bg-accent-100 text-accent-700 text-sm font-medium whitespace-nowrap">
                  200+ 赛事覆盖
                </div>
                <div className="px-4 py-2 rounded-lg bg-secondary-100 text-secondary-700 text-sm font-medium whitespace-nowrap">
                  4.9分 用户好评
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="mb-16" aria-labelledby="rep-title">
          <h2 id="rep-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
            {t('about.reputation')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: '张伟',
                role: '资深体育评论员',
                text: '大满贯App是我日常工作中不可或缺的工具。实时数据更新速度和数据维度的丰富程度都远超同类产品，是我做赛事分析的首选平台。',
                rating: 5,
              },
              {
                name: '李明',
                role: '体育自媒体博主',
                text: '作为一个体育内容创作者，大满贯App为我提供了大量优质素材和数据支撑。直播画质清晰流畅，回看功能也非常实用。',
                rating: 5,
              },
              {
                name: '王芳',
                role: '忠实用户',
                text: '用了大满贯App快三年了，见证了它一步步变得更好。界面越来越简洁好用，功能也越来越丰富。现在看比赛就只用大满贯了！',
                rating: 5,
              },
            ].map((review, idx) => (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-background-100 border border-background-200/70"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">&ldquo;{review.text}&rdquo;</p>
                <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-600 font-bold text-sm">{review.name[0]}</span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">{review.name}</div>
                    <div className="text-xs text-foreground-400">{review.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section id="contact" aria-labelledby="contact-title">
          <h2 id="contact-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
            {t('about.contact')}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Contact Info */}
            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100">
                <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary-500 text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                  <p className="text-foreground-600 text-sm">support@damanjuapp.com</p>
                  <p className="text-foreground-400 text-xs mt-1">商务合作：business@damanjuapp.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100">
                <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-primary-500 text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">客服热线</h3>
                  <p className="text-foreground-600 text-sm">400-888-6666</p>
                  <p className="text-foreground-400 text-xs mt-1">工作时间：周一至周日 9:00 - 21:00</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100">
                <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-500 text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h3>
                  <p className="text-foreground-600 text-sm">北京市朝阳区建国路88号</p>
                  <p className="text-foreground-400 text-xs mt-1">SOHO现代城A座 15层</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <form
                onSubmit={handleContactSubmit}
                data-readdy-form=""
                id="contact-form"
                className="p-6 rounded-2xl bg-background-100 border border-background-200/70"
              >
                <h3 className="text-lg font-bold font-heading text-foreground-950 mb-5">给我们留言</h3>

                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                      placeholder="请输入留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    className="website-alt-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full py-3 rounded-xl bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formSubmitting ? '提交中...' : '提交留言'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="text-sm text-green-600 font-medium text-center py-2 bg-green-50 rounded-lg">
                      留言提交成功！我们会尽快与您联系。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="text-sm text-red-600 font-medium text-center py-2 bg-red-50 rounded-lg">
                      {formError}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}